import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Exhibition, ViewName } from '../types';
import { useExhibitionContext } from '../contexts/ExhibitionContext';
import { formatExhibitionName } from '../utils/formatExhibitionName';

interface NavigationProps {
  currentView: ViewName;
  onViewChange: (viewName: ViewName, artistSlug?: string) => void;
  exhibitions: Exhibition[];
}

const Navigation: React.FC<NavigationProps> = memo(({ currentView, onViewChange, exhibitions }) => {
  const [isExhibitionsOpen, setIsExhibitionsOpen] = useState(false);
  const { selectedExhibition, setSelectedExhibition } = useExhibitionContext();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target) &&
          buttonRef.current && !buttonRef.current.contains(target)) {
        setIsExhibitionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleExhibitionClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onViewChange('exhibition');
  }, [onViewChange]);

  const toggleExhibitionsDropdown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExhibitionsOpen(!isExhibitionsOpen);
  }, [isExhibitionsOpen]);

  const handleExhibitionItemClick = useCallback((exhibition: Exhibition, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Set the selected exhibition
    setSelectedExhibition(exhibition);
    
    // Close the dropdown
    setIsExhibitionsOpen(false);
    
    // Navigate to the exhibition page view
    onViewChange('exhibition');
    
    // Log for debugging
    console.log('Selected exhibition:', exhibition.name, exhibition.slug);
  }, [onViewChange, setSelectedExhibition]);

  const renderExhibitionsDropdown = () => {
    if (exhibitions.length === 0) {
      return (
        <div className="dropdown-empty">אין תערוכות זמינות כעת</div>
      );
    }

    return exhibitions.map(exhibition => {
      const isSelected = selectedExhibition?.id === exhibition.id;
      
      return (
        <a 
          key={exhibition.id} 
          href={`/exhibitions/${exhibition.slug}`} 
          className={`dropdown-item ${isSelected ? 'selected' : ''}`}
          style={{ 
            opacity: isSelected ? 1 : 0.8,
            backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
            borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
            transition: 'opacity 0.2s ease, background-color 0.2s ease, border-left 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!isSelected) {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected) {
              e.currentTarget.style.opacity = '0.8';
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          onClick={(e) => handleExhibitionItemClick(exhibition, e)}
        >
          <div className="dropdown-item-title" style={{ 
            fontWeight: isSelected ? '600' : '400',
            color: isSelected ? '#3b82f6' : 'inherit'
          }}>
            {formatExhibitionName(exhibition.name)}
          </div>
        </a>
      );
    });
  };

  return (
    <nav className="nav">
      <a 
        href="#" 
        className={`nav-item ${currentView === 'exhibition' ? 'nav-item-active' : ''}`}
        onClick={handleExhibitionClick}
      >
        {selectedExhibition ? formatExhibitionName(selectedExhibition.name) : 'מחזור ז׳ | 2025'}
      </a>
      <span className="nav-separator">|</span>
      <div className="nav-dropdown">
        <button 
          className={`nav-item dropdown-btn ${isExhibitionsOpen ? 'active' : ''}`}
          onClick={toggleExhibitionsDropdown}
          ref={buttonRef}
          aria-expanded={isExhibitionsOpen}
          aria-haspopup="true"
        >
          {selectedExhibition ? formatExhibitionName(selectedExhibition.name) : 'תערוכות קודמות'}
          <svg 
            className="dropdown-arrow" 
            width="12" 
            height="12" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            style={{ transform: isExhibitionsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <polyline points="6,9 12,15 18,9"></polyline>
          </svg>
        </button>
        <div 
          className={`dropdown-menu ${isExhibitionsOpen ? 'show' : ''}`}
          ref={dropdownRef}
          role="menu"
          style={{
            borderColor: isExhibitionsOpen ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.2)',
            transition: 'border-color 0.2s ease, opacity 0.2s ease'
          }}
        >
          {exhibitions.length === 0 ? (
            <div className="dropdown-loading">טוען תערוכות...</div>
          ) : (
            renderExhibitionsDropdown()
          )}
        </div>
      </div>
    </nav>
  );
});

Navigation.displayName = 'Navigation';

export default Navigation;